import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import { IPageV2 } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/IPageV2";
import GenericCrudServiceV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/GenericCrudServiceV2";
import HttpClientV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/HttpClientV2";
import { LazoCompany, SimpleUser } from "@/models/LazoCompany";
import { LazoUser } from "@/models/LazoUser";
import { ReAssignCompanyOwner } from "@/models/ReAssignCompanyOwner";
import { Contract } from "@/types/Contract";
import { ProductFlow } from "@/types/ProductFlow";
import { ProductFlowLog } from "@/types/ProductFlowLog";
import { ProductFlowTasks } from "@/types/ProductFlowTasks";
import { ProductManagerManagmentFilters } from "@/types/ProductManagerManagmentFilters";
import { SmallProductFlow } from "@/types/SmallProductFlow";
import { Task } from "@/types/Task";
import { UserFunctionsDTO } from "@/types/UserFunctionsDTO";

class ProductFlowService extends GenericCrudServiceV2<ProductFlow> {
  path: string;
  constructor(path: string) {
    super(path);
    this.path = path;
  }

  async findAllProductFlowsReviewersAndPreparersNames(): Promise<ApiResponseV2<UserFunctionsDTO[]>> {
    const url = `${this.path}/product-flow-filter-data`;
    return HttpClientV2.get(url);
  }

  async getAllProductsFlowsOfUser(userExternalId: string): Promise<ApiResponseV2<SmallProductFlow[]>> {
    return HttpClientV2.get(`${this.path}/all-by-user/${userExternalId}`);
  }

  async getProductFlowTasks(productFlowExternalId: string): Promise<ApiResponseV2<ProductFlowTasks>> {
    return HttpClientV2.get(`${this.path}/${productFlowExternalId}/tasks`);
  }

  getByTaskExternalId(taskExternalId: string): Promise<ApiResponseV2<SmallProductFlow>> {
    return HttpClientV2.get(`${this.path}/task/${taskExternalId}`);
  }

  async getProductFlowUsers(productFlowExternalId: string): Promise<ApiResponseV2<SimpleUser[]>> {
    return HttpClientV2.get(`${this.path}/${productFlowExternalId}/users`);
  }

  async getByLazoProductExternalId(lazoProductExternalId: string, showAll: boolean = false): Promise<ApiResponseV2<SmallProductFlow[]>> {
    const params = new URLSearchParams();
    params.set("showAll", showAll.toString());
    return HttpClientV2.get(`${this.path}/by-product/${lazoProductExternalId}`, { axiosConfig: { params } });
  }

  async addTaskToProductFlow(productFlowExternalId: string, previousTaskExternalId: string, task: Task): Promise<ApiResponseV2<Task[]>> {
    return HttpClientV2.post(`${this.path}/add-task/${productFlowExternalId}/${previousTaskExternalId}`, task);
  }

  async updateProductFlow(productFlowExternalId: string, productFlow: SmallProductFlow): Promise<ApiResponseV2<SmallProductFlow>> {
    return HttpClientV2.put(`${this.path}/update/${productFlowExternalId}`, productFlow);
  }

  finalizeProductFlowService(productFlowExternalId: string): Promise<ApiResponseV2<SmallProductFlow>> {
    return HttpClientV2.put(`${this.path}/finalize-service/${productFlowExternalId}`, null);
  }

  async searchProductFlows(term: string, showAllFlows: boolean): Promise<ApiResponseV2<SmallProductFlow[]>> {
    return HttpClientV2.get(`${this.path}/query?term=${term}&showAll=${showAllFlows}`);
  }

  updateActiveStatus(taskExternalId: string, status: boolean): Promise<ApiResponseV2<Task>> {
    return HttpClientV2.put(`${this.path}/update/active/${taskExternalId}/${status}`, null);
  }

  softDeleteProductFlow(productFlowExternalIds: string[]): Promise<ApiResponseV2<ProductFlow[]>> {
    return HttpClientV2.put(`${this.path}/update/soft-delete`, productFlowExternalIds);
  }

  filterProductFlows(
    filter: ProductManagerManagmentFilters,
    { orderBy, direction }: { orderBy: string; direction: "ASC" | "DESC" } = { orderBy: "startDate", direction: "DESC" },
    page: number = 0,
    pageSize: number = 10,
    showAll: boolean
  ): Promise<ApiResponseV2<IPageV2<SmallProductFlow>>> {
    const params = new URLSearchParams();
    params.set("page", page.toString());
    params.set("pageSize", pageSize.toString());
    params.set("showAll", showAll.toString());
    params.set("orderBy", orderBy);
    params.set("direction", direction);

    return HttpClientV2.post(`${this.path}/filter`, filter, { axiosConfig: { params } });
  }

  async updateCompanyOwnerAndReAssignTasks(dto: ReAssignCompanyOwner): Promise<ApiResponseV2<LazoCompany>> {
    return HttpClientV2.put(`${this.path}/update-company-owner`, dto);
  }

  hasActive(tenantExternalId: string): Promise<ApiResponseV2<boolean>> {
    return HttpClientV2.get(`${this.path}/${tenantExternalId}/has-active`);
  }

  async getContractByProductFlow(productFlowExternalId: string): Promise<ApiResponseV2<Contract | undefined>> {
    return HttpClientV2.get(`${this.path}/${productFlowExternalId}/contract`);
  }

  async getCompanyUsersByProductFlow(productFlowExternalId: string): Promise<ApiResponseV2<LazoUser[]>> {
    return HttpClientV2.get(`${this.path}/${productFlowExternalId}/company-users`);
  }

  async getProductFlowLogs(productFlowExternalId: string): Promise<ApiResponseV2<ProductFlowLog[]>> {
    return HttpClientV2.get(`${this.path}/${productFlowExternalId}/logs`);
  }

  async markFlowAsSeen(productFlowExternalId: string): Promise<ApiResponseV2<void>> {
    return HttpClientV2.post(`${this.path}/${productFlowExternalId}/mark-seen`, null);
  }
}

export default new ProductFlowService("/api/product-flow");
